import { IconAIIcon } from '@/assets/static/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useEncryptedParams } from '@shared/hooks/use-encrypted-params';
import { Badge, Empty, Menu, Skeleton, Tooltip } from 'antd';
import { MenuItemType } from 'antd/es/menu/interface';
import { useLayoutContext } from '@/container/context/layout/layout-context';
import { SidebarNumberProps } from '@modules/channels/channel.types';
import { FaChevronRight } from 'react-icons/fa6';
import clsx from 'clsx';
import { AppAvatar } from '@components/app-avatar';
import { useTranslation } from 'react-i18next';
import { capitalize } from '@shared/utils/capitalize';
import { truncateString } from '@modules/settings/subscription/utils/utils';
import { sortChannelsByDefault } from '@modules/channels/channel.utils';

const style: MenuItemType['style'] = { height: 50, padding: 0, paddingTop: 1.5, textAlign: 'center', lineHeight: 2.8 };

export function CollapsedSidebarNumbersView(props: SidebarNumberProps) {
  const { channelId: queryChannelId = '' } = useParams();
  const {
    assignChannels: { data, initialLoading, hasNextPage },
    layoutContext,
  } = props;
  const { closeSidebarMobileDrawer } = layoutContext;
  const navigate = useNavigate();
  const [, , objToEncryptedParams] = useEncryptedParams();
  const { isSubmenuOverlay, setIsSubmenuOverlay } = useLayoutContext();
  const { t } = useTranslation();
  const numbers = sortChannelsByDefault(data?.data);

  return (
    <div className={clsx('flex flex-col items-center justify-center mt-5', hasNextPage ? 'mb-5' : 'mb-2')}>
      <p className="text-text-600 text-xs uppercase!">{t('number_other')}</p>
      {initialLoading ? (
        <Skeleton active paragraph={false} />
      ) : (
        <>
          {numbers && numbers.length == 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              className="justify-self-start! mx-auto! my-0! mt-4!"
              description={capitalize(t('empty'))}
            />
          ) : (
            <Menu
              selectedKeys={[String(queryChannelId)]}
              style={{ border: 0, width: '100%' }}
              items={numbers
                ?.map((number) => ({
                  key: number?.channel?.id + '',
                  title: '',
                  label: (
                    <Tooltip
                      styles={{ root: { whiteSpace: 'nowrap', minWidth: 'max-content' } }}
                      title={
                        <div className="flex items-center">
                          <span>
                            <AppAvatar src={number?.channel?.country?.flag} className="size-9! border-gray-500!" />
                          </span>
                          <div className="leading-5">
                            <div className="pl-2 truncate max-w-52">
                              {truncateString(number?.channel?.friendlyName, 7)}
                            </div>
                            <div className="pl-1">{number?.channel?.phoneNumber}</div>
                          </div>
                        </div>
                      }
                      placement="right"
                      align={{ offset: [26, 0] }}
                    >
                      <Badge count={number?.channel?.unreadCounter} className="shrink-0">
                        <AppAvatar
                          className="shrink-0 size-7! lg:size-9! border-gray-500!"
                          src={number?.channel?.country?.flag}
                        />
                        {number?.channel?.isAiIntegrated && (
                          <span className="absolute -right-0.5 -bottom-0.5 grid place-items-center rounded-full bg-white p-px lg:p-0.5">
                            <IconAIIcon className="size-2.5 lg:size-3.5 shrink-0!" />
                          </span>
                        )}
                      </Badge>
                    </Tooltip>
                  ),
                  channelId: number?.channel?.id,
                  channelName: number?.channel?.friendlyName + '',
                }))
                .map(({ channelId, channelName, ...item }) => ({
                  ...item,
                  style,
                  onClick: () => {
                    closeSidebarMobileDrawer();
                    navigate({
                      pathname: '/conversations/' + channelId,
                      search: objToEncryptedParams({
                        channelName,
                      }),
                    });
                  },
                }))}
            />
          )}

          {hasNextPage && (
            <Tooltip title={capitalize(t('view_all'))}>
              <div
                id="view-all"
                className={clsx(
                  'hover:bg-primary-50 text-text-400 rounded-full items-center group/view cursor-pointer',
                  'px-2 py-1 border border-text-100 mt-2 gap-2',
                  isSubmenuOverlay === 'channels' ? 'bg-primary-100' : ''
                )}
                onClick={() => setIsSubmenuOverlay((prev) => (prev === 'channels' ? null : 'channels'))}
              >
                <FaChevronRight
                  size={10}
                  style={{ transform: isSubmenuOverlay === 'channels' ? 'rotate(180deg)' : '' }}
                />
              </div>
            </Tooltip>
          )}
        </>
      )}
    </div>
  );
}
